import type { MakeRequired } from '@beesoft/common';
import type { CheckboxRef } from 'beesoft-components';
import clsx from 'clsx';
import dot from 'dot-object';
import type { ReactNode, Ref } from 'react';
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type {
  JsonDataWrapper,
  OdinDataRetrieval,
  OdinDataRetrievalOptions,
} from '../../../common/data-retrieval/data-retrieval-interfaces';
import type { OdinDataSender } from '../../../common/data-retrieval/odin-data-sender';
import useTestTag from '../../../common/hooks/use-test-tag';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import type { ContinuousScrollerRef } from '../../continuous-scroller/continuous-scroller.component';
import ContinuousScroller from '../../continuous-scroller/continuous-scroller.component';
import ContinuousScrollerDataLoadingIndicator from '../../continuous-scroller/continuous-scroller-data-loading-indicator.component';
import type { DynamicFieldComponentProps, DynamicFieldDataProps } from '../../dynamic-form/dynamic-form.interfaces';
import type { JsonData, JsonDataItem } from '../../generic-types';
import ErrorLabel from '../../labels/error-label';
import FieldLabel from '../../labels/field-label';
import type { CheckboxChangeEventType } from '../checkbox/checkbox.component';
import Checkbox from '../checkbox/checkbox.component';
import SelectButton from '../select-button/select-button.component';

export interface CheckboxGroupResult {
  value: unknown;
  text: string;
  color?: string;
}

export interface CheckboxGroupRef {
  clearOptions: () => void;
}

export interface CheckBoxGroupProps
  extends MakeRequired<DynamicFieldComponentProps<Array<CheckboxGroupResult>>, 'name'>,
    DynamicFieldDataProps {
  ref?: Ref<CheckboxGroupRef>;
  className?: string;
  multiSelectCheckboxType?: 'buttons' | 'checkboxes';
  checkboxGroupStyling?: string;
  archivedField?: string;
  showSelectedArchivedOptions?: boolean; // Feature flag to show selected archived options works in combination with archivedField
}

const CheckboxGroup = ({
  ref,
  id,
  name,
  label,
  error,
  value,
  inputRef,
  data,
  textField,
  valueField,
  readOnly,
  required,
  onChange,
  disabled,
  className,
  multiSelectCheckboxType = 'checkboxes',
  labelStyles,
  checkboxGroupStyling,
  archivedField,
  showSelectedArchivedOptions = false,
}: CheckBoxGroupProps) => {
  const [selectedValues, setSelectedValues] = useState<Array<CheckboxGroupResult>>([]);
  const [staticData, setStaticData] = useState<JsonData>();
  const [scrollElement, setScrollElement] = useState<Element>();
  const useStaticData = Array.isArray(data);
  const scrollerRef = useRef<ContinuousScrollerRef>(null);
  const initialSelectedValues = useRef<Array<CheckboxGroupResult>>([]);
  const checkboxRefs = useRef<Map<string, CheckboxRef>>(new Map());

  const isCheckBox = multiSelectCheckboxType === 'checkboxes';

  useEffect(() => {
    if (useStaticData) {
      setStaticData(data);
    }
  }, [data, useStaticData]);

  useEffect(() => {
    const newValues = Array.isArray(value) ? value : [];
    setSelectedValues(newValues);
    if (initialSelectedValues.current.length === 0 && newValues.length > 0) {
      initialSelectedValues.current = newValues;
    }
  }, [value]);

  // Update all checkbox states when selectedValues change
  useEffect(() => {
    checkboxRefs.current.forEach((checkboxRef, checkboxId) => {
      const isSelected = selectedValues.some((item) => item.value == checkboxId);
      checkboxRef?.setChecked?.(isSelected);
    });
  }, [selectedValues, name]);

  const tagCreator = useTestTag(label || name || '');

  const isSelected = (item: JsonDataItem): boolean => {
    return Array.isArray(selectedValues) && selectedValues.some((_item) => _item.value == item[valueField]);
  };

  const wasInitiallySelected = (item: JsonDataItem): boolean => {
    return initialSelectedValues.current.some((_item) => _item.value == item[valueField]);
  };

  const shouldRenderOption = (item: JsonDataItem): boolean => {
    if (!showSelectedArchivedOptions || !archivedField) {
      return true;
    } else {
      const isArchived = !!dot.pick(archivedField, item);

      if (!isArchived) {
        return true;
      } else {
        return wasInitiallySelected(item);
      }
    }
  };

  const handleChange = (checkboxChangeEvent: CheckboxChangeEventType, dataItem: JsonDataItem) => {
    const { value, checked } = checkboxChangeEvent;

    let update: Array<CheckboxGroupResult>;
    if (checked) {
      update = [...selectedValues, { value: value, text: dataItem[textField] as string }];
    } else {
      update = selectedValues.filter((item) => item.value != value);
    }

    setSelectedValues(update);
    onChange?.(update);
  };

  const createCheckboxRef = useCallback((checkboxId: string) => {
    return (ref: CheckboxRef | null) => {
      if (ref) {
        checkboxRefs.current.set(checkboxId, ref);
      } else {
        checkboxRefs.current.delete(checkboxId);
      }
    };
  }, []);

  const containerClasses = clsx('flex flex-col gap-1', className);
  const elementClasses = clsx('flex flex-col gap-1', checkboxGroupStyling, {
    'border border-error': error && !readOnly,
  });

  const renderItem = (item: JsonDataItem, i: number) => {
    const itemValue = String(item[valueField]);
    const isItemSelected = isSelected(item);

    return (
      <Checkbox
        key={`${name}_item_${i}`}
        ref={createCheckboxRef(itemValue)}
        id={name}
        name={name}
        label={item[textField] as string}
        value={item[valueField]}
        inputRef={inputRef}
        initialChecked={isItemSelected}
        onChange={(value) => value && handleChange(value, item)}
        readOnly={readOnly}
        error={error}
        disabled={disabled}
        showError={false}
      />
    );
  };

  const renderItems = (finalData: JsonData) => finalData.filter(shouldRenderOption).map(renderItem);

  const renderSelectButtons = (finalData?: JsonData) => {
    const filteredData = finalData?.filter(shouldRenderOption);

    return (
      <SelectButton
        id={name}
        textField={textField}
        valueField={valueField}
        inputRef={inputRef}
        value={selectedValues || []}
        data={filteredData}
        onChange={onChange}
        readOnly={readOnly}
        error={error}
        disabled={disabled}
        showError={false}
      />
    );
  };

  const renderStaticData = () => (
    <div className={elementClasses} ref={tagCreator('checkbox-group')}>
      {isCheckBox ? staticData && renderItems(staticData) : staticData && renderSelectButtons(staticData)}
    </div>
  );

  const renderScrollerItems = (data: JsonData): ReactNode | ReactNode[] => {
    return (
      <div className={elementClasses} ref={tagCreator('scroller-checkbox-group')}>
        {isCheckBox ? renderItems(data) : renderSelectButtons(data)}
      </div>
    );
  };

  const getData = useCallback(
    (dataSender: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
      if (data && !useStaticData) {
        const dataOptions: OdinDataRetrievalOptions = {
          ...options,
          fieldId: id,
          fieldType: 'MULTISELECTCHECKBOX',
        };

        data?.getData?.(dataSender, dataOptions);
      }
    },
    [data, id, useStaticData]
  );

  const dataRetriever: OdinDataRetrieval = useMemo(() => {
    return { getData };
  }, [getData]);

  const dataLoadIndicator = useMemo(() => <ContinuousScrollerDataLoadingIndicator />, []);

  const renderContinuousScroller = () => (
    <ContinuousScroller
      ref={scrollerRef}
      dataRetriever={dataRetriever}
      scrollingElement={scrollElement}
      dataLoadIndicator={dataLoadIndicator}
    >
      {renderScrollerItems}
    </ContinuousScroller>
  );

  useImperativeHandle(ref, () => ({
    clearOptions,
  }));

  const clearOptions = () => {
    scrollerRef.current?.__dangerouslyResetScrollerData();
  };

  const onScrollElementCreated = (element: HTMLDivElement) => {
    setScrollElement(element);
  };

  return (
    <div
      className={containerClasses}
      ref={(element) => {
        if (element) onScrollElementCreated(element);
      }}
    >
      {label && name && (
        <div className="flex">
          <FieldLabel label={label} name={name} labelStyles={labelStyles} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      {useStaticData ? renderStaticData() : renderContinuousScroller()}
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </div>
  );
};

export default CheckboxGroup;
